import { QuestionBankRepository } from '../api/repository/question-bank';

import { createCountCache } from './countCache';
import type { IzQuestion } from './izteach';
import { fetchOriginalRows } from './originalPage';
import { boardSourceQuery, pageLimit, pageParams, serverFilter } from './paging';
import type { ServerQuery } from './paging';
import { matchesServerQuery } from './serverMatch';

/**
 * The read side of Kho bài tập: one page of rows and the filter's count, apart from each other.
 *
 * Measured on prod (280k questions): the server walks the newest-first index until it has _limit
 * matches, so a page is fast when the filter matches many rows and walks the whole bank when it asks
 * past the last match; /question/count always scans. Hence: the count is asked once per filter and
 * never holds the page back, and once it says the page is short, exactly the rows left are asked for.
 */

/** One count per filter, apart from the page (countCache.ts). */
const counts = createCountCache((filter) => QuestionBankRepository.count(filter));

/** Kho đề gốc pages around every AI row (originalPage.ts); past this many its page edges stop being exact. */
const AI_ROWS_CAP = 500;

let aiRowsRequest: Promise<IzQuestion[]> | null = null;

/** Every AI row of the bank, newest edit first: counted first, since a _limit above the matches walks the bank (_limit 40: 0.5 s, 500: 20 s). */
function allAiRows(): Promise<IzQuestion[]> {
  if (aiRowsRequest) return aiRowsRequest;
  const query = { ...serverFilter({}, null), ...boardSourceQuery('ai-quiz-supporter') };
  const request = QuestionBankRepository.count(query).then((total) => {
    // the real fix is a "not equal" on the exam gateway; until then say so rather than drift quietly
    if (total > AI_ROWS_CAP) console.warn(`Kho đề gốc: ${total} AI rows, over ${AI_ROWS_CAP}; page edges may repeat or skip a row`);
    const limit = Math.min(total, AI_ROWS_CAP);
    return limit > 0 ? QuestionBankRepository.rows({ ...query, ...pageParams(1, limit) }) : [];
  });
  aiRowsRequest = request;
  request.catch(() => { if (aiRowsRequest === request) aiRowsRequest = null; });
  return request;
}

/** A write changed the bank: the count and the AI rows are read again. */
export function bankChanged() {
  counts.forget();
  aiRowsRequest = null;
}

export interface PageRead {
  /** True when this read sent the count; false when the filter's count is already out or in. */
  fresh: boolean;
  /** The filter's total for the tab; null when it failed. */
  total: Promise<number | null>;
  /** Whether `total` still belongs to the filter on screen. */
  isCurrent: () => boolean;
  /** The page's rows; `known` is the total already on screen for this filter, if any. */
  rows: (known: number | null) => Promise<IzQuestion[]>;
}

/** `original` is Kho đề gốc: the filter minus its AI rows, which the server cannot leave out itself. */
export function readPage(filter: ServerQuery, original: boolean, page: number, size: number): PageRead {
  const count = counts.countFor(filter);
  const aiMatched = original
    ? allAiRows().then((rows) => rows.filter((row) => matchesServerQuery(row, filter)))
    : Promise.resolve([] as IzQuestion[]);
  const total = original
    ? Promise.all([count.request, aiMatched.then((rows) => rows.length, () => null)])
      .then(([all, ai]) => (all === null || ai === null ? null : Math.max(0, all - ai)))
    : count.request;

  // `limit` rows of this page; none is no request at all
  const pageRows = (limit: number): Promise<IzQuestion[]> => {
    if (limit <= 0) return Promise.resolve([]);
    const request = original
      ? aiMatched.then((ai) => fetchOriginalRows(QuestionBankRepository.rows, filter, page, size, ai, limit))
      : QuestionBankRepository.rows({ ...filter, ...pageParams(page, size), _limit: limit });
    // the request that loses the race below must not surface as an unhandled rejection
    request.catch(() => undefined);
    return request;
  };

  return {
    fresh: count.fresh,
    total,
    isCurrent: () => counts.isCurrent(count.key),
    rows(known) {
      if (known !== null) return pageRows(pageLimit(known, page, size));
      // a count that lands first and says the page is short sends the exact request too; first answer wins
      return Promise.race([
        pageRows(size),
        total.then((landed) => (landed !== null && landed < page * size
          ? pageRows(pageLimit(landed, page, size))
          : new Promise<IzQuestion[]>(() => undefined))),
      ]);
    },
  };
}
